import { useQueryClient } from '@tanstack/react-query'

import {
  useInvestigationsServiceApproveInvestigationTestEndpoint,
  useInvestigationsServiceGetInvestigationTestsEndpointKey,
  useInvestigationsServiceGetListSpecimenInvestigationsEndpointKey,
  useInvestigationsServiceGetSpecimenEndpointKey,
  useInvestigationsServiceSubmitInvestigationTestToValidationEndpoint
} from 'src/api/queries'
import {
  ApiError,
  InvestigationTestsListItemDto,
  InvestigationTestStatus
} from 'src/api/requests'
import { VALIDATE_RESULT_PERMISSIONS } from 'src/app-constants'
import { message } from 'src/components'
import { useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

// Shared sending logic for investigation tests. Users who may validate a result
// send it straight to the doctor; everyone else submits it to validation. Used
// both per investigation (InvestigationTestsCard) and per specimen
// (SpecimenDetailsPage).
export const useSendInvestigationTests = () => {
  const { t } = useLocales()
  const queryClient = useQueryClient()
  const { hasPermission } = useUser()

  const canValidateResults = hasPermission(VALIDATE_RESULT_PERMISSIONS)

  // Sending a result can change the test status, the parent investigation's
  // status and the specimen status, so refresh all three.
  const invalidateTests = () =>
    [
      useInvestigationsServiceGetInvestigationTestsEndpointKey,
      useInvestigationsServiceGetListSpecimenInvestigationsEndpointKey,
      useInvestigationsServiceGetSpecimenEndpointKey
    ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))

  const { mutate: approve } =
    useInvestigationsServiceApproveInvestigationTestEndpoint({
      onSuccess: () => {
        message.success(t('investigations.successfully_send_result'))
        invalidateTests()
      },
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  const { mutate: sendToValidation } =
    useInvestigationsServiceSubmitInvestigationTestToValidationEndpoint({
      onSuccess: () => {
        message.success(t('investigations.successfully_send_to_validation'))
        invalidateTests()
      },
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  // A ready test can still be sent when it has a result, is not yet approved
  // and not rejected. Validators can send any status; others only NEW tests.
  const canSend = (test: InvestigationTestsListItemDto) =>
    !!test.results?.length &&
    !test.isApproved &&
    test.status !== InvestigationTestStatus.REJECTED &&
    (canValidateResults || test.status === InvestigationTestStatus.NEW)

  const sendTest = (id: string) =>
    canValidateResults ? approve({ id }) : sendToValidation({ id })

  const getSendableTests = (tests?: InvestigationTestsListItemDto[]) =>
    tests?.filter(canSend) ?? []

  const sendTests = (tests: InvestigationTestsListItemDto[]) =>
    tests.forEach((test) => test.id && sendTest(test.id))

  return {
    canValidateResults,
    canSend,
    sendTest,
    getSendableTests,
    sendTests
  }
}
